/**
 * Flattens error messages from a nested API response.
 *
 * This function processes an error object where field names are keys
 * and values are error messages, potentially nested.
 * It ensures that all error messages are flattened into a readable string format.
 *
 * @param errors - The error object with field names as keys and error messages as values.
 *
 * @returns A formatted string with flattened error messages.
 *
 * @example
 * const error = {
 *   name: ["This field may not be blank."],
 *   category: ["This field is required."],
 *   materials: [ { type: ["Invalid type."], quantity: ["Must be a number."] } ]
 * };
 *
 * const flattenedErrors = flattenErrors(error);
 * console.log(flattenedErrors);
 * // Output:
 * // name: This field may not be blank.
 * // category: This field is required.
 * // materials.type: Invalid type.
 * // materials.quantity: Must be a number.
 */
export const flattenErrors = (errors: Record<string, any>): string => {
    if (!errors || typeof errors !== "object") {
        return "Unknown error occurred.";
    }

    const processField = (field: string, messages: any): string[] => {
        if (Array.isArray(messages)) {
            return messages.flatMap((msg) => {
                if (typeof msg === "object") {
                    return Object.entries(msg).flatMap(([nestedField, nestedMessages]) => {
                        return processField(`${field}.${nestedField}`, nestedMessages);
                    });
                }
                return `${field}: ${msg}`;
            });
        }

        if (typeof messages === "object") {
            return Object.entries(messages).flatMap(([nestedField, nestedMessages]) => {
                return processField(`${field}.${nestedField}`, nestedMessages);
            });
        }

        if (typeof messages === "string") {
            return [`${messages}`];
        }
        return [];
    };

    return Object.entries(errors)
        .flatMap(([field, messages]) => processField(field, messages))
        .join("\n");
};